import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, TrendingUp } from 'lucide-react'
import { Button } from '@components/ui/Button'
import { useAuth } from '@hooks/useAuth'
import { useLanguage } from '@/contexts/LanguageContext'
import { LanguageSwitcher } from '@components/shared/LanguageSwitcher'
import { ThemeToggle } from '@components/shared/ThemeToggle'
import { cn } from '@lib/utils'

/**
 * Barre de navigation du site public.
 *
 * Trois partis pris, chacun en reponse a un defaut constate :
 *
 * 1. Le point de rupture est gere par CSS seul. L'ancienne version combinait un
 *    `useIsMobile()` (matchMedia, donc faux au premier rendu) ET les classes
 *    `md:` de Tailwind. Le double verrou faisait apparaitre la version bureau
 *    pendant un instant sur telephone, avant que l'effet ne s'execute. Les
 *    classes suffisent : le JavaScript ne sert plus qu'a ouvrir le menu.
 *
 * 2. Le nom de marque est un seul noeud de texte. Il etait auparavant rendu par
 *    `FancyText`, qui le duplique — une couche fantome plus une lettre par
 *    `<span>`. A la copie, au lecteur d'ecran et aux robots d'indexation, cela
 *    donnait « TrackMarkete » suivi de « T r a c k M a r k e t e ». Le reflet
 *    au survol remplace l'animation et ne coute rien a l'accessibilite.
 *
 * 3. Le premier lien pointe vers « / ». Il affichait « Accueil » mais menait a
 *    `/#features` : depuis la page d'accueil, cliquer dessus faisait descendre
 *    vers la section fonctionnalites au lieu de remonter.
 */
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { isAuthenticated } = useAuth()
  const { t } = useLanguage()
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 100)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Un changement de page doit refermer le menu : sans cela il reste ouvert
  // par-dessus la page d'arrivee.
  useEffect(() => setIsMenuOpen(false), [location.pathname])

  // Echap referme, et le defilement de la page est bloque tant que le panneau
  // couvre l'ecran — sinon le fond defile sous les doigts.
  useEffect(() => {
    if (!isMenuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false)
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [isMenuOpen])

  const navLinks = [
    { label: t('nav.features'), href: '/' },
    { label: t('nav.shop'), href: '/shop' },
    { label: t('nav.pricing'), href: '/pricing' },
    { label: t('nav.howItWorks'), href: '/how-it-works' },
    { label: t('nav.faq'), href: '/faq' },
    { label: t('nav.affiliate'), href: '/affiliate' },
  ]

  const isActive = (href: string) => location.pathname === href

  const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/40 ' +
    'dark:focus-visible:ring-white/40 focus-visible:ring-offset-2 ' +
    'focus-visible:ring-offset-white dark:focus-visible:ring-offset-black'

  return (
    <>
      {/* Premier element focusable de la page : il permet d'atteindre le
          contenu sans parcourir toute la navigation au clavier. */}
      <a
        href="#contenu"
        className={cn(
          'sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50',
          'focus:rounded-full focus:bg-black focus:px-5 focus:py-2.5 focus:label',
          'focus:!text-[11px] focus:text-white dark:focus:bg-white dark:focus:text-black'
        )}
      >
        {t('nav.skipToContent')}
      </a>

      <nav
        aria-label={t('nav.mainLabel')}
        className={cn(
          'fixed top-0 left-0 right-0 z-40 transition-all duration-300 border-b',
          isScrolled
            ? 'bg-white/90 dark:bg-black/85 border-black/10 dark:border-white/10 backdrop-blur-lg py-3'
            : 'bg-white/70 dark:bg-black/60 border-transparent backdrop-blur-md py-4'
        )}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/"
              className={cn('flex items-center gap-3 group rounded-sm', focusRing)}
            >
              <span
                aria-hidden="true"
                className="w-10 h-10 shrink-0 bg-black dark:bg-chrome flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
              >
                <TrendingUp className="h-5 w-5 text-white dark:text-black" />
              </span>
              {/* Un seul noeud de texte : c'est ce que lisent la copie, les
                  lecteurs d'ecran et les robots. Le reflet au survol est
                  purement decoratif et porte par le degrade. */}
              <span className="relative text-xl font-head font-bold tracking-tight text-black dark:text-white">
                TrackMarkete
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/70 to-transparent opacity-0 transition-all duration-700 group-hover:translate-x-full group-hover:opacity-100 dark:via-white/30"
                />
              </span>
            </Link>

            <div className="hidden xl:flex items-center gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={isActive(link.href) ? 'page' : undefined}
                  className={cn(
                    'label !tracking-[0.06em] whitespace-nowrap transition-colors duration-200 pb-1 border-b rounded-sm',
                    focusRing,
                    isActive(link.href)
                      ? 'text-black dark:text-white border-black dark:border-white'
                      : 'text-gray-500 dark:text-neutral-500 border-transparent hover:text-black dark:hover:text-white'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden xl:flex items-center gap-2">
              <ThemeToggle />
              <LanguageSwitcher variant="desktop" />
              {isAuthenticated ? (
                <Button
                  as={Link}
                  to="/dashboard"
                  className={cn(
                    'bg-black text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200 rounded-full px-5 py-2.5 label !text-[11px] whitespace-nowrap transition-all',
                    focusRing
                  )}
                >
                  {t('nav.myAccount')}
                </Button>
              ) : (
                <>
                  <Button
                    as={Link}
                    to="/auth/login"
                    variant="ghost"
                    className={cn(
                      'text-gray-600 hover:text-black dark:text-neutral-300 dark:hover:text-white rounded-full px-4 py-2.5 label !text-[11px] whitespace-nowrap',
                      focusRing
                    )}
                  >
                    {t('nav.login')}
                  </Button>
                  <Button
                    as={Link}
                    to="/auth/signup"
                    className={cn(
                      'bg-black text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200 rounded-full px-5 py-2.5 label !text-[11px] whitespace-nowrap transition-all',
                      focusRing
                    )}
                  >
                    {t('nav.signup')}
                  </Button>
                </>
              )}
            </div>

            <div className="flex xl:hidden items-center gap-1">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setIsMenuOpen((open) => !open)}
                aria-expanded={isMenuOpen}
                aria-controls="menu-mobile"
                aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                className={cn(
                  'p-2.5 rounded-full transition-all text-gray-700 hover:text-black hover:bg-gray-100',
                  'dark:text-neutral-300 dark:hover:text-white dark:hover:bg-white/10',
                  focusRing
                )}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div
            id="menu-mobile"
            className="xl:hidden absolute top-full left-0 right-0 max-h-[calc(100vh-100%)] overflow-y-auto bg-white dark:bg-black backdrop-blur-xl shadow-xl shadow-black/5 dark:shadow-black/40 border-t border-gray-200/50 dark:border-white/10 animate-fade-in"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={isActive(link.href) ? 'page' : undefined}
                  className={cn(
                    'px-4 py-3 label transition-all border-b border-black/5 dark:border-white/5 rounded-sm',
                    focusRing,
                    isActive(link.href)
                      ? 'text-black dark:text-white'
                      : 'text-gray-600 dark:text-neutral-400 hover:text-black dark:hover:text-white'
                  )}
                >
                  {link.label}
                </Link>
              ))}

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                <LanguageSwitcher variant="mobile" />
              </div>

              <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                {isAuthenticated ? (
                  <Button
                    as={Link}
                    to="/dashboard"
                    className={cn('bg-black text-white dark:bg-white dark:text-black rounded-full', focusRing)}
                    fullWidth="true"
                  >
                    {t('nav.myAccount')}
                  </Button>
                ) : (
                  <>
                    <Button
                      as={Link}
                      to="/auth/login"
                      variant="outline"
                      className={cn('rounded-full', focusRing)}
                      fullWidth="true"
                    >
                      {t('nav.login')}
                    </Button>
                    <Button
                      as={Link}
                      to="/auth/signup"
                      className={cn('bg-black text-white dark:bg-white dark:text-black rounded-full', focusRing)}
                      fullWidth="true"
                    >
                      {t('nav.signup')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  )
}
